import React, { useState } from 'react';
import { Layers, Download, Info, Calendar, CheckCircle, AlertCircle, Clock } from 'lucide-react';
import Button from '../../../../../components/ui/Button';
import { formatCurrency } from '../../../../../utils/formatting';

/**
 * Компонент для отображения графика выплат субсидий
 */
const SubsidySchedule = ({ schedule, results, showFullSchedule, setShowFullSchedule }) => {
  const [activeTab, setActiveTab] = useState('general');
  
  if (!schedule || schedule.length === 0 || !results) {
    return null;
  }

  // Для отображения только первого года, если не показан полный график
  const displaySchedule = showFullSchedule ? schedule : schedule.slice(0, 4); // Показываем первый год (4 квартала)

  // Начальные итоговые суммы
  const totals = {
    amount: schedule.reduce((sum, item) => sum + item.amount, 0)
  };

  // Перевод статуса платежа на русский
  const getStatusTranslation = (status) => {
    const statusMap = {
      'approved': 'Одобрено',
      'processing': 'В обработке',
      'planned': 'Запланировано'
    };
    return statusMap[status] || status;
  };

  // Получение класса для статуса
  const getStatusClass = (status) => {
    switch (status) {
      case 'approved':
        return 'text-green-600 dark:text-green-400';
      case 'processing':
        return 'text-amber-600 dark:text-amber-400';
      case 'planned':
        return 'text-gray-600 dark:text-gray-400';
      default:
        return 'text-gray-600 dark:text-gray-400';
    }
  };

  // Получение иконки для статуса
  const StatusIcon = ({ status }) => {
    switch (status) {
      case 'approved':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'processing':
        return <Clock className="w-4 h-4 text-amber-500" />;
      case 'planned':
        return <Calendar className="w-4 h-4 text-gray-500" />;
      default:
        return <Info className="w-4 h-4 text-gray-500" />;
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm p-5 mt-8">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center">
          <Layers className="w-5 h-5 mr-2 text-emerald-500" />
          График выплат субсидий ({schedule.length} кварталов)
        </h3>

        {/* Кнопка переключения полного/краткого графика */}
        {schedule.length > 4 && (
          <Button
            size="sm"
            onClick={() => setShowFullSchedule(prev => !prev)}
            variant="outline"
            aria-expanded={showFullSchedule}
          >
            {showFullSchedule ? 'Показать краткий график' : `Показать все ${schedule.length} кварталов`}
          </Button>
        )}
      </div>

      {/* Вкладки для разных типов отображения */}
      <div className="flex space-x-1 border-b border-gray-200 dark:border-gray-700 mb-4">
        <button
          onClick={() => setActiveTab('general')}
          className={`py-2 px-4 text-sm font-medium rounded-t-lg ${
            activeTab === 'general'
              ? 'text-emerald-600 dark:text-emerald-400 border-b-2 border-emerald-500 dark:border-emerald-400'
              : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
          }`}
        >
          Общий график
        </button>
        <button
          onClick={() => setActiveTab('detailed')}
          className={`py-2 px-4 text-sm font-medium rounded-t-lg ${
            activeTab === 'detailed'
              ? 'text-emerald-600 dark:text-emerald-400 border-b-2 border-emerald-500 dark:border-emerald-400'
              : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
          }`}
        >
          Детализация выплат
        </button>
      </div>

      {/* Информационная подсказка */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800 rounded p-3 mb-4 flex items-start">
        <Info className="text-blue-500 dark:text-blue-400 w-5 h-5 mr-2 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-blue-700 dark:text-blue-300">
          <p>{showFullSchedule 
            ? "Полный график всех выплат субсидий по кварталам. Прокрутите таблицу вправо для просмотра всех данных." 
            : `Показаны первые ${displaySchedule.length} кварталов графика выплат. Нажмите "Показать все кварталы" для просмотра полного графика.`
          }</p>
        </div>
      </div>

      {/* Контейнер таблицы с прокруткой */}
      <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700 max-h-[600px] subsidy-schedule-table">
        <table className="w-full text-sm text-left text-gray-700 dark:text-gray-300 min-w-[800px]">
          <thead className="text-xs text-gray-700 dark:text-gray-300 uppercase bg-gray-100 dark:bg-gray-700 sticky top-0 z-[1] shadow-sm">
            <tr>
              <th scope="col" className="px-3 py-2 font-semibold text-center">Год</th>
              <th scope="col" className="px-3 py-2 font-semibold text-center">Квартал</th>
              <th scope="col" className="px-3 py-2 font-semibold">Дата</th>

              {activeTab === 'general' && (
                <>
                  <th scope="col" className="px-3 py-2 font-semibold text-right">Сумма</th>
                  <th scope="col" className="px-3 py-2 font-semibold">Статус</th>
                </>
              )}

              {activeTab === 'detailed' && (
                <>
                  <th scope="col" className="px-3 py-2 font-semibold text-right">Сумма выплаты</th>
                  <th scope="col" className="px-3 py-2 font-semibold">Описание</th>
                  <th scope="col" className="px-3 py-2 font-semibold">Статус платежа</th>
                </>
              )}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
            {displaySchedule.map((item, index) => (
              <tr
                key={index}
                className={`hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors duration-100 ${
                  item.paymentStatus === 'approved' ? 'bg-green-50 dark:bg-green-900/20' : 
                  item.paymentStatus === 'processing' ? 'bg-amber-50 dark:bg-amber-900/20' : ''
                }`}
              >
                <td className="px-3 py-2 whitespace-nowrap text-center font-medium">{item.year}</td>
                <td className="px-3 py-2 whitespace-nowrap text-center">{item.quarter}</td>
                <td className="px-3 py-2 whitespace-nowrap">
                  {item.date.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                </td>

                {activeTab === 'general' && (
                  <>
                    <td className="px-3 py-2 whitespace-nowrap text-right font-medium">{formatCurrency(item.amount, 0)}</td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      <div className={`flex items-center ${getStatusClass(item.paymentStatus)}`}>
                        <StatusIcon status={item.paymentStatus} />
                        <span className="ml-1.5">{getStatusTranslation(item.paymentStatus)}</span>
                      </div>
                    </td>
                  </>
                )}

                {activeTab === 'detailed' && (
                  <>
                    <td className="px-3 py-2 whitespace-nowrap text-right font-medium">{formatCurrency(item.amount, 0)}</td>
                    <td className="px-3 py-2">{item.description}</td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      <div className={`flex items-center ${getStatusClass(item.paymentStatus)}`}>
                        <StatusIcon status={item.paymentStatus} />
                        <span className="ml-1.5">{getStatusTranslation(item.paymentStatus)}</span>
                      </div>
                    </td>
                  </>
                )}
              </tr>
            ))}

            {/* Итоговая строка */}
            <tr className="bg-gray-50 dark:bg-gray-700 font-semibold">
              <td className="px-3 py-2 whitespace-nowrap text-center" colSpan="3">Итого:</td>

              {activeTab === 'general' && (
                <>
                  <td className="px-3 py-2 whitespace-nowrap text-right">{formatCurrency(totals.amount, 0)}</td>
                  <td className="px-3 py-2 whitespace-nowrap">-</td>
                </>
              )}

              {activeTab === 'detailed' && (
                <>
                  <td className="px-3 py-2 whitespace-nowrap text-right">{formatCurrency(totals.amount, 0)}</td>
                  <td className="px-3 py-2 whitespace-nowrap" colSpan="2">Общая сумма субсидий</td>
                </>
              )}
            </tr>
          </tbody>
        </table>
      </div>

      {/* Кнопка показа полного графика (если он скрыт и больше предпросмотра) */}
      {!showFullSchedule && schedule.length > 4 && (
        <div className="text-center mt-4">
          <Button
            onClick={() => setShowFullSchedule(true)}
            icon={<Layers className="w-4 h-4" />}
            variant="link"
          >
            Показать все {schedule.length} кварталов
          </Button>
        </div>
      )}

      {/* Легенда статусов */}
      <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-2 border-t border-gray-200 dark:border-gray-700 pt-4">
        <div className="flex items-center">
          <CheckCircle className="w-4 h-4 text-green-500 mr-1.5" />
          <span className="text-xs text-gray-600 dark:text-gray-400">Одобрено - выплата произведена</span>
        </div>
        <div className="flex items-center">
          <Clock className="w-4 h-4 text-amber-500 mr-1.5" />
          <span className="text-xs text-gray-600 dark:text-gray-400">В обработке - на рассмотрении</span>
        </div>
        <div className="flex items-center">
          <Calendar className="w-4 h-4 text-gray-500 mr-1.5" />
          <span className="text-xs text-gray-600 dark:text-gray-400">Запланировано - по графику</span>
        </div>
      </div>
    </div>
  );
};

export default SubsidySchedule;